import styled, { css, keyframes } from 'styled-components'
import { margin, MarginProps } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'

export const Container = styled.section<MarginProps>`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 76px 40px 0 40px;

  @media screen and (min-width: ${themeGet('breakpoints.2')}) {
    padding: 92px 40px 0 40px;
  }

  ${margin}
`

export const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'copy'
    'visual';
  align-items: start;

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    grid-template-columns: minmax(0, 1.1fr) minmax(360px, 0.9fr);
    grid-template-areas: 'visual copy';
    align-items: center;
    gap: 40px;
  }
`

export const HeroCopy = styled.div`
  grid-area: copy;
  display: flex;
  flex-direction: column;
  max-width: 560px;
  padding: 40px 0;

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    padding-top: 0;
    height: 100%;
  }
`

export const ClientLogos = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  > img {
    display: block;
    max-height: 32px;
    width: auto;
    object-fit: contain;
    filter: grayscale(1);
    opacity: 0.6;
  }

  @media screen and (min-width: ${themeGet('breakpoints.2')}) {
    > img {
      max-height: 48px;
    }
  }
`

export const Visual = styled.div`
  grid-area: visual;
  position: relative;
  display: flex;
  justify-content: center;

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    justify-content: flex-start;
    align-self: start;
  }
`

export const VisualFrame = styled.div`
  position: relative;
  width: min(100%, 760px);
  overflow: hidden;
  border-radius: 16px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(
      to bottom,
      ${themeGet('colors.surface.screen')} 0%,
      transparent 85%
    );
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(
      to top,
      ${themeGet('colors.surface.screen')} 0%,
      transparent 85%
    );
    pointer-events: none;
    z-index: 1;
  }

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    width: min(56vw, 543px);
  }
`

const scrollUp = keyframes`
  0%   { transform: translateY(0); }
  100% { transform: translateY(-50%); }
`

const scrollDown = keyframes`
  0%   { transform: translateY(-50%); }
  100% { transform: translateY(0); }
`

export const VisualColumns = styled.div`
  display: flex;
  align-items: flex-start;
  height: 480px;

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    height: 680px;
  }
`

type VisualColumnProps = {
  direction: 'up' | 'down'
}

const visualColumnAnimation = ({ direction }: VisualColumnProps) => {
  const animationName = direction === 'up' ? scrollUp : scrollDown

  return css`
    animation: ${animationName} 40s linear infinite;
  `
}

export const VisualColumn = styled.div<VisualColumnProps>`
  flex: 1;
  display: flex;
  flex-direction: column;

  > img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  /* NOTE: Negative margins compensate for excessive spacing caused by image shadows.
     And image shadows must overlap a little to look nice. */
  &:not(:first-child) {
    margin-left: -4%;
  }

  &:not(:last-child) {
    margin-right: -4%;
  }

  ${visualColumnAnimation}
`
